import { useEffect, createContext } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Modal,
  Space,
  Spin,
  Card,
  Row,
  Col,
  Flex,
  Dropdown,
  MenuProps,
} from "antd";

import { DownOutlined } from "@ant-design/icons";

import ObjectForm from "components/Admin/ObjectForm";

import { RootState } from "Redux/store";
import { fetchOption, updateOption, createOption, deleteOption } from "Redux/Admin/DecisionManager/Options/action";

import { settings, initialSettings, initialValues } from './const';
import { ADMIN_PATH } from "utils/routes";

import type { IOption } from 'Redux/Admin/DecisionManager/Options/type';

const Option = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const { entities, loading, error } = useSelector((state: RootState) => state.fetchOptions);

  const [modal, contextHolder] = Modal.useModal();

  const ReachableContext = createContext<string | null>(null);

  useEffect(() => {
    if (id && !isNew) {
      dispatch(fetchOption(id));
    }
  }, [dispatch, isNew, id]);

  const onFinish = (values: any) => {
    if (isNew) {
      dispatch(createOption(values, handleCreate));
    }
    else if (id) {
      dispatch(updateOption(values, id));
    }
  };

  const handleCreate = (project: IOption) => {
    if (project && project.id) {
      navigate(`${ADMIN_PATH}/decision-manager/options/${project.id}`);
    }
  }

  if (!id) {
    navigate(`${ADMIN_PATH}/decision-manager/options`, { replace: true });
    return null;
  }

  const option = isNew ? initialValues : entities[id];

  if (!option) {
    return <Spin />;
  }

  const menuItems: MenuProps['items'] = [
    {
      label: 'Delete Option',
      key: '2',
      danger: true,
    }
  ];

  const modalConfig = {
    title: 'Delete the option?',
    content: <span>Are you sure you want to delete the option <b>{option.id}</b>?</span>,
    confirmLoading: true
  };

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === '2') {
      const confirmed = await modal.confirm(modalConfig);

      if (confirmed) {
        dispatch(deleteOption(id, () => {
          navigate(`${ADMIN_PATH}/decision-manager/options`, { replace: true });
        }));
      }
    }
  };

  const menuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  };

  return (
    <ReachableContext.Provider value="Light">
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Row>
          <Col span={12}>
            <Flex
              gap="middle"
              align="center"
              justify="space-between"
              style={{ marginBottom: 16 }}
            >
              <Link to={`${ADMIN_PATH}/decision-manager/options`}>Back</Link>
            </Flex>

            <Card title={`${isNew ? 'Create' : '' } Option`} extra={
              isNew ? null : <Dropdown menu={menuProps} placement="bottom">
                <Button>
                  <Space>
                    Manage Option
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            }>
              <ObjectForm
                data={option}
                errorData={error}
                isNew={isNew}
                settings={isNew ? initialSettings : settings}
                loading={loading}
                onFinish={onFinish}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {contextHolder}
    </ReachableContext.Provider>
  );
}

export default Option;
